import _extends from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import React from "react";
var __jsx = React.createElement;
import { Checkbox, FileButton, Input, Radio } from "@mantine/core";
import { faPaperclip } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { FormDate, FormDateTime, FormInput, FormSelect, FormTextarea } from "@/components/form";
import { choicesToOptions } from "@/utils/form";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { BloodGlucoseField } from "./blood-glucose-field";
import { BloodPressureField } from "./blood-pressure-field";
import { LkwField } from "./lkw-field";
import { NihssChoiceField } from "./nihss-choice-field";
import { NihssScoreField } from "./nihss-score-field";
import { SingleSelectTabField } from "./single-select-tab-field";
import { SmokingCalculatorField } from "./smoking-calculator-field";
/** Renders a single form observation by its response type. */
export function FormField(_ref) {
  var _field$observation;
  var field = _ref.field,
    value = _ref.value,
    disabled = _ref.disabled,
    _onChange = _ref.onChange;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var label = t((_field$observation = field.observation) !== null && _field$observation !== void 0 ? _field$observation : "");
  var text = typeof value === "string" || typeof value === "number" ? String(value) : "";
  if (field.response_type === "text" || field.response_type === "number") {
    var _field$placeholder;
    return /*#__PURE__*/React.createElement(FormInput, {
      label: label,
      type: field.response_type,
      value: text,
      required: field.required,
      disabled: disabled,
      placeholder: t((_field$placeholder = field.placeholder) !== null && _field$placeholder !== void 0 ? _field$placeholder : ""),
      onChange: function onChange(event) {
        return _onChange(event.currentTarget.value);
      },
      onBlur: function onBlur() {
        return trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
      },
      "data-testid": "field-".concat(field.id)
    });
  }
  if (field.response_type === "select" || field.response_type === "dropdown") {
    var _field$options, _field$placeholder2;
    return /*#__PURE__*/React.createElement(FormSelect, {
      label: label,
      data: (_field$options = field.options) !== null && _field$options !== void 0 ? _field$options : choicesToOptions(field.choices, t),
      value: text || null,
      required: field.required,
      disabled: disabled,
      searchable: true,
      clearable: true,
      withinPortal: true,
      placeholder: t((_field$placeholder2 = field.placeholder) !== null && _field$placeholder2 !== void 0 ? _field$placeholder2 : ""),
      onChange: function onChange(next) {
        trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
        _onChange(next !== null && next !== void 0 ? next : "");
      },
      "data-testid": "field-".concat(field.id)
    });
  }
  if (field.response_type === "textarea") {
    var _field$placeholder3;
    return /*#__PURE__*/React.createElement(FormTextarea, {
      label: label,
      minRows: 3,
      value: text,
      required: field.required,
      disabled: disabled,
      placeholder: t((_field$placeholder3 = field.placeholder) !== null && _field$placeholder3 !== void 0 ? _field$placeholder3 : ""),
      onChange: function onChange(event) {
        return _onChange(event.currentTarget.value);
      },
      onBlur: function onBlur() {
        return trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
      },
      "data-testid": "field-".concat(field.id)
    });
  }
  if (field.response_type === "datetime" && field.id === "last_known_well_time") {
    return /*#__PURE__*/React.createElement(LkwField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "date") {
    return /*#__PURE__*/React.createElement(FormDate, {
      label: label,
      value: text,
      withAsterisk: field.required,
      onChange: _onChange,
      testId: "field-".concat(field.id)
    });
  }
  if (field.response_type === "datetime") {
    return /*#__PURE__*/React.createElement(FormDateTime, {
      label: label,
      value: text,
      withAsterisk: field.required,
      onChange: _onChange,
      testId: "field-".concat(field.id)
    });
  }
  if (field.response_type === "choice") {
    return /*#__PURE__*/React.createElement(Radio.Group, {
      label: label,
      value: text,
      onChange: function onChange(next) {
        trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
        _onChange(next);
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-2 pt-1"
    }, choicesToOptions(field.choices, t).map(function (option) {
      return /*#__PURE__*/React.createElement(Radio, {
        key: option.value,
        value: option.value,
        label: option.label,
        disabled: disabled || option.disabled,
        "data-testid": "field-".concat(field.id, "-").concat(option.value)
      });
    })));
  }
  if (field.response_type === "multiple_choice") {
    var selected = Array.isArray(value) ? value : [];
    return /*#__PURE__*/React.createElement(Checkbox.Group, {
      label: label,
      value: selected,
      onChange: function onChange(next) {
        trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
        _onChange(next);
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-2 pt-1"
    }, choicesToOptions(field.choices, t).map(function (option) {
      return /*#__PURE__*/React.createElement(Checkbox, {
        key: option.value,
        value: option.value,
        label: option.label,
        disabled: disabled || option.disabled,
        "data-testid": "field-".concat(field.id, "-").concat(option.value)
      });
    })));
  }
  if (field.response_type === "bool") {
    return /*#__PURE__*/React.createElement(Checkbox, {
      label: label,
      checked: Boolean(value),
      disabled: disabled,
      onChange: function onChange(event) {
        trackEvent(ANALYTICS_EVENTS.FIELD, {
          fieldId: field.id
        });
        _onChange(event.currentTarget.checked);
      },
      "data-testid": "field-".concat(field.id)
    });
  }
  if (field.response_type === "upload") {
    var fileName = value instanceof File ? value.name : text;
    return /*#__PURE__*/React.createElement(Input.Wrapper, {
      label: label,
      required: field.required
    }, /*#__PURE__*/React.createElement(FileButton, {
      onChange: function onChange(file) {
        return file && _onChange(file);
      }
    }, function (props) {
      return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        variant: "secondary",
        size: "sm",
        leftIcon: faPaperclip,
        disabled: disabled,
        onClick: function onClick(event) {
          var _props$onClick;
          trackEvent(ANALYTICS_EVENTS.FIELD, {
            fieldId: field.id
          });
          (_props$onClick = props.onClick) === null || _props$onClick === void 0 || _props$onClick.call(props, event);
        },
        "data-testid": "field-".concat(field.id)
      }), fileName || t("choose_file", {
        defaultValue: "Choose file"
      }));
    }));
  }
  if (field.response_type === "blood_pressure") {
    return /*#__PURE__*/React.createElement(BloodPressureField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "blood_glucose") {
    return /*#__PURE__*/React.createElement(BloodGlucoseField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "nihss_choice") {
    return /*#__PURE__*/React.createElement(NihssChoiceField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "nihss_input") {
    return /*#__PURE__*/React.createElement(NihssScoreField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "choice_tabs") {
    return /*#__PURE__*/React.createElement(SingleSelectTabField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  if (field.response_type === "smoking_calculator") {
    return /*#__PURE__*/React.createElement(SmokingCalculatorField, {
      field: field,
      value: value,
      disabled: disabled,
      onChange: _onChange
    });
  }
  return null;
}